/**
 * Is the chart canvas actually the size of the screen, and is its GL context alive.
 *
 * Edd opened the app after a night's sleep and got a chart filling the top half
 * of the screen with black underneath it, while every button sat where it should
 * (05 Aug, build 304). The buttons are React, the chart is a WebGL canvas, and the
 * two disagreeing about how tall the screen is narrows it down to the canvas. The
 * black is the page background showing through where the canvas stops.
 *
 * MapLibre does watch its own container and resize itself, so this should not be
 * possible, and that is exactly why it is worth watching rather than reasoning
 * about. A resize that was dropped, a ResizeObserver callback that threw, or a
 * WebView whose insets landed after layout all end up in the same place, and from
 * a screenshot they are indistinguishable.
 *
 * So this does two things, and deliberately does not try to work out which of them
 * was needed:
 *
 *   1. It compares the canvas with its container and resizes when they disagree.
 *      Self-healing, because a skipper wanting the chart back does not care whose
 *      fault it was.
 *   2. It writes down every disagreement it found, and every WebGL context that
 *      was lost or came back. So the next report says which of these it was
 *      instead of being another photograph of a black rectangle.
 *
 * The checks are cheap (four numbers off elements that are already in memory) and
 * they run often at boot and rarely afterwards, because boot is when the screen is
 * still settling and it is where this went wrong.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import { loadJson, saveJson } from '@/store/persist';

export const CANVAS_HEALTH_LS = 'acp.canvasHealth.v1';

/** How far out the canvas has to be before it counts. One pixel is rounding. */
const TOLERANCE_PX = 2;

/** Kept small: what went wrong is in the last few of these, not the first. */
const MAX_EVENTS = 20;

export type CanvasEventKind = 'resized' | 'context-lost' | 'context-restored';

export interface CanvasEvent {
  /** Wall clock, ms. */
  t: number;
  kind: CanvasEventKind;
  /** Container size in CSS pixels, which is what the canvas should have been. */
  containerW: number;
  containerH: number;
  /** What the canvas actually was. */
  canvasW: number;
  canvasH: number;
  /** Seconds since the map was created, so "at boot" is distinguishable. */
  ageS: number;
}

function load(): CanvasEvent[] {
  const raw = loadJson<CanvasEvent[]>(CANVAS_HEALTH_LS, []);
  return Array.isArray(raw) ? raw : [];
}

/** Everything recorded, oldest first. */
export function canvasEvents(): CanvasEvent[] {
  return load();
}

export function clearCanvasEvents(): void {
  saveJson(CANVAS_HEALTH_LS, []);
}

export function recordCanvasEvent(e: CanvasEvent): void {
  const all = [...load(), e];
  saveJson(CANVAS_HEALTH_LS, all.slice(-MAX_EVENTS));
}

/**
 * One line for a bug report, or null when there is nothing to say.
 *
 * Says WHEN as well as what, because a resize two seconds after boot is the
 * screen settling and is uninteresting, while one nine hours in is the app
 * having been broken on screen for nine hours.
 */
export function canvasHealthSummary(): string | null {
  const all = load();
  if (!all.length) return null;
  const resized = all.filter((e) => e.kind === 'resized');
  const lost = all.filter((e) => e.kind === 'context-lost').length;
  const parts: string[] = [];
  if (resized.length) {
    const last = resized[resized.length - 1];
    parts.push(
      `${resized.length} canvas size fix(es). Last: canvas was ${last.canvasW}x${last.canvasH} ` +
        `in a ${last.containerW}x${last.containerH} screen, ${Math.round(last.ageS)}s after the map started`,
    );
  }
  if (lost) {
    const back = all.filter((e) => e.kind === 'context-restored').length;
    parts.push(`${lost} WebGL context loss(es), ${back} came back`);
  }
  return parts.length ? parts.join('. ') : null;
}

/**
 * Write down a lost or restored WebGL context.
 *
 * MapLibre already deals with the recovery, and MapView already listens; what it
 * did with the news was console.warn, and a phone has no console. A lost context
 * looks exactly like a chart that failed to draw, so without this it is invisible
 * in a bug report and indistinguishable from every other reason for a blank chart.
 */
export function recordContext(
  kind: 'context-lost' | 'context-restored',
  container: { clientWidth: number; clientHeight: number } | null,
  canvas: { clientWidth: number; clientHeight: number } | null,
  ageS = 0,
  now: () => number = Date.now,
): void {
  recordCanvasEvent({
    t: now(),
    kind,
    containerW: container?.clientWidth ?? 0,
    containerH: container?.clientHeight ?? 0,
    canvasW: canvas?.clientWidth ?? 0,
    canvasH: canvas?.clientHeight ?? 0,
    ageS,
  });
}

/** The canvas is out by more than rounding, so it needs putting right. */
export function canvasIsWrongSize(
  container: { clientWidth: number; clientHeight: number },
  canvas: { clientWidth: number; clientHeight: number },
): boolean {
  // A container of zero is a map that is not on screen (a hidden tab, a sheet
  // covering it, teardown mid-flight). Resizing to nothing would be its own bug,
  // so nothing that measures zero counts as wrong.
  if (container.clientWidth < 1 || container.clientHeight < 1) return false;
  return (
    Math.abs(container.clientWidth - canvas.clientWidth) > TOLERANCE_PX ||
    Math.abs(container.clientHeight - canvas.clientHeight) > TOLERANCE_PX
  );
}

/**
 * How often to look, in ms, at a given age since the map started.
 *
 * Often for the first half minute, because that is when the screen is still
 * settling and it is when this went wrong. Then rarely, because after that the
 * only thing that changes the viewport is a rotation or a keyboard, and both of
 * those fire their own events anyway.
 */
export function checkIntervalMs(ageS: number): number {
  if (ageS < 30) return 1_000;
  if (ageS < 120) return 5_000;
  return 30_000;
}

/**
 * Watch the map's canvas and put it right when it drifts. Returns a stop function.
 *
 * `now` and the timer are injectable so this is testable without waiting around.
 */
export function watchMapCanvas(
  map: MaplibreMap,
  container: HTMLElement,
  now: () => number = Date.now,
): () => void {
  const started = now();
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const ageS = () => (now() - started) / 1000;

  const check = () => {
    if (stopped) return;
    let canvas: HTMLCanvasElement | null = null;
    try {
      canvas = map.getCanvas();
    } catch {
      return; // map torn down under us
    }
    if (!canvas) return;
    if (!canvasIsWrongSize(container, canvas)) return;
    recordCanvasEvent({
      t: now(),
      kind: 'resized',
      containerW: container.clientWidth,
      containerH: container.clientHeight,
      canvasW: canvas.clientWidth,
      canvasH: canvas.clientHeight,
      ageS: ageS(),
    });
    try {
      map.resize();
    } catch {
      // A resize that throws is worth knowing about but not worth taking the
      // chart down for. The event above is already written.
    }
  };

  const tick = () => {
    check();
    if (stopped) return;
    timer = setTimeout(tick, checkIntervalMs(ageS()));
  };
  timer = setTimeout(tick, checkIntervalMs(0));

  // A resize we can be told about beats one we have to go and look for, so take
  // both: the events fire immediately, the poll catches whatever they missed,
  // which is the case we are actually chasing.
  const observer =
    typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => check());
  observer?.observe(container);
  window.addEventListener('resize', check);
  window.addEventListener('orientationchange', check);
  document.addEventListener('visibilitychange', check);

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    observer?.disconnect();
    window.removeEventListener('resize', check);
    window.removeEventListener('orientationchange', check);
    document.removeEventListener('visibilitychange', check);
  };
}
